import { apiClient } from './apiClient';
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
  UserProfile,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ApiResponse
} from './types/api';

/**
 * Authentication Service
 * Handles user authentication, registration, and profile management
 */
class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/login', credentials, { skipAuth: true });
      
      // Backend returns { success: true, data: { user, token, refreshToken, expiresIn }, message }
      if (response.success && response.data) {
        const authData = response.data;

        // S3.5 — store distinct access + refresh tokens. Pre-S3.5 backend
        // returned the same JWT in both slots; the `|| authData.token`
        // fallback keeps an old backend revision compatible during deploy
        // crossover.
        if (authData.token) {
          apiClient.setAuthTokens(authData.token, authData.refreshToken || authData.token);
        }

        const authResponse = {
          user: authData.user,
          token: authData.token,
          authToken: authData.token,
          refreshToken: authData.refreshToken || authData.token,
          expiresIn: authData.expiresIn,
          expires_in: authData.expiresIn,
          token_type: 'Bearer',
          ...(authData.checkoutUrl && { checkoutUrl: authData.checkoutUrl }),
          ...(authData.pendingPayment && { pendingPayment: authData.pendingPayment }),
        };

        return authResponse;
      }

      throw new Error('Invalid login response');

    } catch (error) {
      throw error;
    }
  }

  /**
   * Register new user account
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    // Map frontend fields to backend fields
    const backendData: Record<string, string> = {
      email: userData.email,
      password: userData.password,
      name: `${userData.first_name} ${userData.last_name}`.trim(),
    };
    if (userData.plan_intent) {
      backendData.plan_intent = userData.plan_intent;
    }
    if (userData.billing_interval) {
      backendData.billing_interval = userData.billing_interval;
    }
    // Mandatory terms + privacy acceptance. Backend rejects with 400
    // TERMS_NOT_ACCEPTED if not strictly `true`. The RegisterForm UI
    // already enforces this — forwarded as a typed boolean so legacy
    // callers (none today) cannot accidentally bypass it.
    (backendData as Record<string, unknown>).terms_accepted = userData.terms_accepted === true;
    // Marketing contact preference (opt-out checkbox on RegisterForm).
    // We always send it when the form has set it — backend defaults to
    // `true` when omitted to match the opt-out semantics. Forwarded as
    // boolean (not stringified) so the backend type guard accepts it.
    if (typeof userData.marketing_consent === 'boolean') {
      (backendData as Record<string, unknown>).marketing_consent = userData.marketing_consent;
    }
    
    console.log('🔐 AUTH DEBUG - Sending registration request:', { email: backendData.email, name: backendData.name });
    
    const response = await apiClient.post('/auth/register', backendData, { skipAuth: true });
    
    console.log('🔐 AUTH DEBUG - Registration response structure:', {
      hasData: !!response.data,
      hasSuccess: !!response.data?.success,
      hasDataData: !!response.data?.data,
      responseKeys: response.data ? Object.keys(response.data) : [],
      fullResponse: response.data
    });
    
    // Backend uses createApiResponse which returns: { success: true, data: authResponse, message, timestamp }
    // So the auth data is in response.data (not response.data.data)
    let authData: any = null;
    
    if (response.success && response.data) {
      authData = response.data;
    }
    
    if (authData && (authData.user || authData.token)) {
      console.log('🔐 AUTH DEBUG - Found auth data:', { hasUser: !!authData.user, hasToken: !!authData.token });
      
      // S3.5 — store distinct access + refresh tokens (see /login).
      if (authData.token) {
        apiClient.setAuthTokens(authData.token, authData.refreshToken || authData.token);
      }

      // Merge subscription into user object so AuthContext sees it immediately
      // (matches the shape returned by /auth/me)
      const user = authData.user?.subscription
        ? authData.user
        : { ...authData.user, ...(authData.subscription && { subscription: authData.subscription }) };

      return {
        user,
        token: authData.token,
        authToken: authData.token,
        refreshToken: authData.refreshToken || authData.token,
        expiresIn: authData.expiresIn,
        expires_in: authData.expiresIn,
        token_type: 'Bearer',
        ...(authData.checkoutUrl && { checkoutUrl: authData.checkoutUrl }),
      };
    }

    console.error('🔐 AUTH DEBUG - Invalid registration response structure:', response.data);
    throw new Error(`Invalid registration response: ${JSON.stringify(response.data)}`);
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      // S3.5 — pass the refresh token so the backend can revoke its row.
      // Without this, an attacker who exfiltrated the refresh token before
      // logout could keep minting access tokens until the row expires.
      const refreshToken = localStorage.getItem('refreshToken');
      await apiClient.post('/auth/logout', refreshToken ? { refreshToken } : {});
    } catch (error) {
      // Continue with local logout even if server call fails
      console.warn('Server logout failed, proceeding with local logout:', error);
    } finally {
      apiClient.clearAuthTokens();
      localStorage.removeItem('user_profile');
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    console.log('🔐 AUTH SERVICE - getCurrentUser called');
    
    // 🔍 DEBUG: Check token state before API call
    const authToken = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');
    console.log('🔐 AUTH SERVICE - Token state before /auth/me call:', {
      hasAuthToken: !!authToken,
      hasRefreshToken: !!refreshToken,
      authTokenLength: authToken?.length || 0,
      authTokenPreview: authToken ? `${authToken.substring(0, 10)}...` : 'none',
      timestamp: new Date().toISOString()
    });
    
    try {
      console.log('🔐 AUTH SERVICE - Making /auth/me API call...');
      const response = await apiClient.get('/auth/me');
      
      console.log('🔐 AUTH SERVICE - /auth/me response received:', {
        hasResponse: !!response,
        success: response?.success,
        hasData: !!response?.data,
        responseKeys: response ? Object.keys(response) : [],
        dataKeys: response?.data ? Object.keys(response.data) : []
      });
      
      // Backend returns { success: true, data: userData, message, timestamp }
      if (response.success && response.data) {
        console.log('🔐 AUTH SERVICE - getCurrentUser successful, returning user data');
        return response.data;
      }
      
      console.error('🔐 AUTH SERVICE - Invalid getCurrentUser response structure:', response);
      throw new Error('Failed to get current user');
    } catch (error) {
      console.error('🔐 AUTH SERVICE - getCurrentUser error:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText,
        responseData: (error as any)?.response?.data,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  /**
   * Get detailed user profile
   */
  async getUserProfile(): Promise<UserProfile> {
    const response = await apiClient.get<ApiResponse<UserProfile>>('/auth/profile');
    
    // Cache profile in localStorage for offline access
    if (response.data?.data) {
      localStorage.setItem('user_profile', JSON.stringify(response.data.data));
    }
    
    return response.data?.data!;
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: UpdateProfileRequest): Promise<UserProfile> {
    const response = await apiClient.put<ApiResponse<UserProfile>>('/auth/profile', profileData);
    
    // Update cached profile
    if (response.data?.data) {
      localStorage.setItem('user_profile', JSON.stringify(response.data.data));
    }
    
    return response.data?.data!;
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    await apiClient.post('/auth/change-password', passwordData);
  }

  /**
   * Request password reset
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await apiClient.post('/auth/forgot-password', data);
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await apiClient.post('/auth/reset-password', data);
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<void> {
    await apiClient.post('/auth/verify-email', { token });
  }

  /**
   * Resend email verification
   */
  async resendVerification(): Promise<void> {
    await apiClient.post('/auth/resend-verification');
  }

  /**
   * Refresh access token. NOTE: the runtime refresh path is in apiClient's
   * 401 interceptor (`performTokenRefresh`); this method is kept for
   * completeness / tests / direct callers.
   */
  async refreshToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // S3.5 — body field is `refreshToken` (camelCase). Backend's response
      // shape is { success, data: { token, refreshToken, ... } } via the
      // apiClient wrapper.
      const response: any = await apiClient.post('/auth/refresh', { refreshToken });
      const newAccess = response?.data?.token ?? response?.token;
      const newRefresh = response?.data?.refreshToken ?? response?.refreshToken;

      if (newAccess) {
        localStorage.setItem('authToken', newAccess);
        if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
        return newAccess;
      }

      return null;
    } catch (error) {
      // Clear invalid tokens (both keys; refresh failure means session is gone)
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const authToken = localStorage.getItem('authToken');
    const accessToken = localStorage.getItem('access_token');
    const rnkrocketToken = localStorage.getItem('rnkrocket_auth_token');
    
    // 🔍 DEBUG: Log token storage state for debugging
    console.log('🔐 AUTH SERVICE - isAuthenticated check:', {
      hasAuthToken: !!authToken,
      hasAccessToken: !!accessToken,
      hasRnkrocketToken: !!rnkrocketToken,
      authTokenLength: authToken?.length || 0,
      accessTokenLength: accessToken?.length || 0,
      rnkrocketTokenLength: rnkrocketToken?.length || 0,
      timestamp: new Date().toISOString()
    });
    
    return !!authToken;
  }

  /**
   * Get cached user profile
   */
  getCachedProfile(): UserProfile | null {
    try {
      const cached = localStorage.getItem('user_profile');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Get current refresh token (S3.5 — camelCase key now canonical)
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Enable two-factor authentication
   */
  async enableTwoFactor(): Promise<{ qr_code: string; backup_codes: string[] }> {
    const response = await apiClient.post<ApiResponse<{ qr_code: string; backup_codes: string[] }>>('/auth/2fa/enable');
    return response.data?.data!;
  }

  /**
   * Verify and confirm two-factor authentication setup
   */
  async confirmTwoFactor(code: string): Promise<void> {
    await apiClient.post('/auth/2fa/confirm', { code });
  }

  /**
   * Disable two-factor authentication
   */
  async disableTwoFactor(password: string): Promise<void> {
    await apiClient.post('/auth/2fa/disable', { password });
  }

  /**
   * Generate new backup codes for 2FA
   */
  async generateBackupCodes(): Promise<string[]> {
    const response = await apiClient.post<ApiResponse<{ backup_codes: string[] }>>('/auth/2fa/backup-codes');
    return response.data?.data?.backup_codes || [];
  }

  /**
   * Get user's active sessions
   */
  async getActiveSessions(): Promise<Array<{
    id: string;
    device: string;
    location: string;
    ip_address: string;
    last_active: string;
    is_current: boolean;
  }>> {
    const response = await apiClient.get<ApiResponse<Array<{
      id: string;
      device: string;
      location: string;
      ip_address: string;
      last_active: string;
      is_current: boolean;
    }>>>('/auth/sessions');
    return response.data?.data || [];
  }

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/auth/sessions/${sessionId}`);
  }

  /**
   * Revoke all other sessions (keep current)
   */
  async revokeAllOtherSessions(): Promise<void> {
    await apiClient.post('/auth/sessions/revoke-all');
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;